import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import CloseCircleLineIcon from "remixicon-react/CloseCircleLineIcon";

import {
  createAttribute,
  updateAttribute,
  getProductCount
} from "../../../core/actionCreators/attributeActionCreator";

const AttributeListingForm = forwardRef((props: any, ref) => {
  const dispatch = useDispatch();
  const { pageMode, attribute, onCountUpdate } = props;

  const [attributePayload, setAttributePayload] = useState({
    id: "",
    title: "",
    friendlyUrl: "",
    metaTitle: "",
    metaDescription: "",
    titleDescription: "",
    pageDescription: "",
    category: "",
    categoryName: "",
    searchTerm: "",
    isActive: "",
    attributes: [
      {
        id: uuidv4(),
        key: "",
        value: "",
      },
    ],
  } as any);

  const addAttributesHandler = () => {
    setAttributePayload({
      ...attributePayload,
      attributes: [
        ...attributePayload.attributes,
        {
          id: uuidv4(),
          key: "",
          value: "",
        },
      ],
    });
  };

  const onAttributeChangeHandler = (
    id: string,
    type: string,
    value: string
  ) => {
    const attributeIndex = attributePayload.attributes.findIndex(
      (d: any) => d.id === id
    );
    if (attributeIndex === -1) {
      toast.error("Invalid field");
      return;
    }
    const attributes = [...attributePayload.attributes];
    const attribute = { ...attributes[attributeIndex] };
    if (type === "KEY") {
      attribute.key = value;
    }
    if (type === "VALUE") {
      attribute.value = value;
    }
    attributes[attributeIndex] = attribute;
    setAttributePayload({ ...attributePayload, attributes });
  };

  const removeAttributeHandler = (id: string) => {
    const filteredAttributes = attributePayload.attributes.filter(
      (d: any) => d.id !== id
    );
    setAttributePayload({
      ...attributePayload,
      attributes: filteredAttributes,
    });
  };

  useEffect(() => {
    if (pageMode === "EDIT" && Object.keys(attribute).length && typeof attribute !== "string") {
      const transformedAttributes = [];
      if (attribute.attributes && typeof attribute.attributes === "object" && !Array.isArray(attribute.attributes)) {
        for (const [key, value] of Object.entries(attribute.attributes)) {
          transformedAttributes.push({
            id: uuidv4(),
            key: key,
            value: Array.isArray(value) ? value.join(", ") : value,
          });
        }
      }
      setAttributePayload({
        ...attribute,
        attributes: transformedAttributes.length > 0 ? transformedAttributes : [
          {
            id: uuidv4(),
            key: "",
            value: "",
          },
        ],
      });
    }
  }, [attribute]);

  const getTransformedPayload = () => {
    let payload = { ...attributePayload };

    // Transform attributes array to object
    const transformedAttributes: any = {};
    if (Array.isArray(payload.attributes)) {
      payload.attributes.forEach((item: any) => {
        const trimmedKey = item.key.trim();
        if (trimmedKey) {
          const valuesArray = item.value
            .split(",")
            .map((v: string) => v.trim())
            .filter((v: string) => v !== "");
          transformedAttributes[trimmedKey] = valuesArray;
        }
      });
    }
    payload.attributes = transformedAttributes;

    if (payload.isActive !== "") {
      payload.isActive = typeof payload.isActive === "string" ? JSON.parse(payload.isActive) : payload.isActive;
    }

    return payload;
  };

  const formSubmitHandler = () => {
    const payload = getTransformedPayload();

    if (pageMode === "CREATE") {
      delete payload.id;
      dispatch(
        createAttribute(payload, () => {
          toast.success("Saved successfully.");
        })
      );
    } else {
      dispatch(
        updateAttribute(payload, () => {
          toast.success("Updated successfully.");
        })
      );
    }
  };

  const checkProductCountHandler = () => {
    const payload = getTransformedPayload();
    dispatch(getProductCount(payload, (res: any) => {
      if (res && res.status === true) {
        toast.success(`Current product count for these attributes: ${res.data}`);
        onCountUpdate && onCountUpdate(res.data);
      }
    }));
  };

  useImperativeHandle(ref, () => ({
    checkProductCount: checkProductCountHandler,
  }));



  return (
    <>
      <div className="p-4 pb-8">
        <div className="w-full bg-white py-4 px-2.5 shadow-[0_1px_4px_rgba(0,0,0,.2)] rounded mb-5">
          <form>
            <div className="flex w-full mb-6 last:mb-0">
              <div className="w-1/2 px-2">
                <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                  Title
                </label>
                <input
                  className="w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm font-light"
                  type="text"
                  value={attributePayload.title}
                  onChange={(e) =>
                    setAttributePayload({
                      ...attributePayload,
                      title: e.target.value,
                    })
                  }
                />
              </div>
              <div className="w-1/2 px-2">
                <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                  Friendly URL
                </label>
                <input
                  className="w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm font-light"
                  type="text"
                  value={attributePayload.friendlyUrl}
                  onChange={(e) =>
                    setAttributePayload({
                      ...attributePayload,
                      friendlyUrl: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="flex w-full mb-6 last:mb-0">
              <div className="w-1/2 px-2">
                <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                  Meta Title
                </label>
                <textarea
                  rows={3}
                  className="w-full rounded border text-sm text-[#555] border-[#EAEAEA] px-2.5 outline-none bg-white font-light"
                  value={attributePayload.metaTitle}
                  onChange={(e) =>
                    setAttributePayload({
                      ...attributePayload,
                      metaTitle: e.target.value,
                    })
                  }
                ></textarea>
              </div>
              <div className="w-1/2 px-2">
                <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                  Meta Title Description
                </label>
                <textarea
                  rows={3}
                  className="w-full rounded border text-sm text-[#555] border-[#EAEAEA] px-2.5 outline-none bg-white font-light"
                  value={attributePayload.metaDescription}
                  onChange={(e) =>
                    setAttributePayload({
                      ...attributePayload,
                      metaDescription: e.target.value,
                    })
                  }
                ></textarea>
              </div>
            </div>
            <div className="flex w-full mb-6 last:mb-0">
              <div className="w-1/2 px-2">
                <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                  Title Description
                </label>
                <textarea
                  rows={3}
                  className="w-full rounded border text-sm text-[#555] border-[#EAEAEA] px-2.5 outline-none bg-white font-light"
                  value={attributePayload.titleDescription}
                  onChange={(e) =>
                    setAttributePayload({
                      ...attributePayload,
                      titleDescription: e.target.value,
                    })
                  }
                ></textarea>
              </div>
              <div className="w-1/2 px-2">
                <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                  Page Description
                </label>
                <textarea
                  rows={3}
                  className="w-full rounded border text-sm text-[#555] border-[#EAEAEA] px-2.5 outline-none bg-white font-light"
                  value={attributePayload.pageDescription}
                  onChange={(e) =>
                    setAttributePayload({
                      ...attributePayload,
                      pageDescription: e.target.value,
                    })
                  }
                ></textarea>
              </div>
            </div>
            <div className="flex w-full mb-6 last:mb-0">
              <div className="w-1/2 px-2">
                <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                  Category Code
                </label>
                <input
                  className="w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm font-light"
                  type="text"
                  value={attributePayload.category}
                  onChange={(e) =>
                    setAttributePayload({
                      ...attributePayload,
                      category: e.target.value,
                    })
                  }
                />
              </div>
              <div className="w-1/2 px-2">
                <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                  Category Name
                </label>
                <input
                  className="w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm font-light"
                  type="text"
                  value={attributePayload.categoryName}
                  onChange={(e) =>
                    setAttributePayload({
                      ...attributePayload,
                      categoryName: e.target.value,
                    })
                  }
                />
              </div>
            </div>
            <div className="flex w-full mb-6 last:mb-0">
              <div className="w-1/2 px-2">
                <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                  Search Term
                </label>
                <input
                  className="w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm font-light"
                  type="text"
                  value={attributePayload.searchTerm}
                  onChange={(e) =>
                    setAttributePayload({
                      ...attributePayload,
                      searchTerm: e.target.value,
                    })
                  }
                />
              </div>
              <div className="w-1/2 px-2">
                <label className="block text-xs mb-1.5 text-[#5E5D5D] font-medium">
                  In Active
                </label>
                <div className="flex items-end">
                  <div className="flex items-center h-9">
                    <input
                      className="w-4 h-4 rounded border  border-[#3c3c3c] px-2.5 outline-none "
                      type="radio"
                      name="isActive"
                      value="true"
                      onChange={(e) =>
                        setAttributePayload({
                          ...attributePayload,
                          isActive: true,
                        })
                      }
                      checked={attributePayload.isActive === true || attributePayload.isActive === "true"}
                    />
                    <span className="ml-2 text-[#3c3c3c] text-sm font-light">
                      Active
                    </span>
                  </div>
                  <div className="flex items-center ml-4 h-9">
                    <input
                      className="w-4 h-4 rounded border  border-[#3c3c3c] px-2.5 outline-none "
                      type="radio"
                      name="isActive"
                      value="false"
                      onChange={(e) =>
                        setAttributePayload({
                          ...attributePayload,
                          isActive: false,
                        })
                      }
                      checked={attributePayload.isActive === false || attributePayload.isActive === "false"}
                    />
                    <span className="ml-2 text-[#3c3c3c] text-sm font-light">
                      Inactive
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Wrapped */}
            <div className="px-2">
              {Array.isArray(attributePayload?.attributes) &&
                attributePayload.attributes.map((d: any, index: number) => (
                  <div key={d.id} className="flex w-full mb-4 last:mb-0 items-start">
                    <div className="w-1/2 px-2 pl-0">
                      <label className="block text-[10px] mb-1 text-[#8E8E8E] font-medium uppercase tracking-wider">
                        Key
                      </label>
                      <input
                        onChange={(e) =>
                          onAttributeChangeHandler(d.id, "KEY", e.target.value)
                        }
                        placeholder="e.g. brand, color, size"
                        value={d.key}
                        className="w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm font-light focus:border-[#3B86FF] transition-colors"
                        type="text"
                      />
                    </div>
                    <div className="w-1/2 px-2">
                      <label className="block text-[10px] mb-1 text-[#8E8E8E] font-medium uppercase tracking-wider">
                        Values (comma separated)
                      </label>
                      <input
                        onChange={(e) =>
                          onAttributeChangeHandler(
                            d.id,
                            "VALUE",
                            e.target.value
                          )
                        }
                        placeholder="e.g. 3M, Ansell"
                        value={d.value}
                        className="w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm font-light focus:border-[#3B86FF] transition-colors"
                        type="text"
                      />
                    </div>

                    <div
                      className="cursor-pointer mt-7 text-[#FF4D4F] hover:text-[#FF7875] transition-colors"
                      onClick={() => removeAttributeHandler(d.id)}
                    >
                      <CloseCircleLineIcon size={20} />
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addAttributesHandler}
                  className="h-8 px-4 bg-[#3B86FF1a] text-[#3B86FF] text-[11px] font-bold rounded border border-[#3B86FF] hover:bg-[#3B86FF] hover:text-white transition-all duration-200 focus:outline-none flex items-center gap-1.5"
                >
                  <span className="text-base">+</span> ADD ATTRIBUTES
                </button>
            </div>
          </form>
        </div>
        <div className="mt-8 flex justify-end px-2">
          <button
            onClick={formSubmitHandler}
            className="h-10 px-10 bg-[#3B86FF] text-white text-sm font-bold rounded shadow-[0_4px_14px_0_rgba(59,134,255,0.39)] hover:bg-[#2A75EE] hover:shadow-[0_6px_20px_rgba(59,134,255,0.23)] transition-all duration-200 focus:outline-none uppercase tracking-wide"
          >
            {pageMode === "CREATE" ? "Save Listing" : "Update Listing"}
          </button>
        </div>
      </div>
    </>
  );
});
export default AttributeListingForm;
